import { useEffect, useState, useMemo } from "react";
import Header from "../components/Header";
import AppCard from "../components/AppCard";
import { api, extractErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// enviCoM Lite app ids — used to split into product-line sections
const LITE_APP_IDS = new Set(["envicom_lite_versions", "envicom_lite_controllers"]);

function isLite(app) {
  return LITE_APP_IDS.has(app.id);
}

export default function Hub() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/apps");
        setApps(data.apps);
        setAllApps(data.all_apps);
      } catch (e) {
        setError(extractErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { fourZero, lite } = useMemo(() => {
    const accessibleIds = new Set(apps.map((a) => a.id));
    const lockedApps = allApps.filter((a) => !accessibleIds.has(a.id));

    const withLock = [
      ...apps.map((a) => ({ ...a, locked: false })),
      ...lockedApps.map((a) => ({ ...a, locked: true })),
    ];

    // Preserve original order from allApps
    const orderMap = new Map(allApps.map((a, i) => [a.id, i]));
    withLock.sort(
      (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0)
    );

    return {
      fourZero: withLock.filter((a) => !isLite(a)),
      lite: withLock.filter((a) => isLite(a)),
    };
  }, [apps, allApps]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <section className="animate-fade-up">
          <div className="text-envicom-muted text-xs font-mono uppercase tracking-[0.2em]">
            {user?.role === "super_admin" ? "Super admin · Full access" : `Welcome, ${user?.name?.split(" ")[0] || "engineer"}`}
          </div>
          <h1 className="mt-3 font-mono text-3xl md:text-4xl font-bold text-envicom-text tracking-tight">
            Your Tools
          </h1>
          <p className="mt-2 text-envicom-muted">Select a tool to get started.</p>
        </section>

        {error && (
          <div data-testid="hub-error" className="mt-6 px-3 py-2 rounded bg-envicom-error/10 border border-envicom-error/30 text-envicom-error text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <section className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="apps-grid-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-envicom-surface border border-envicom-border rounded-md p-6 h-[180px] animate-pulse" />
            ))}
          </section>
        ) : (
          <>
            {fourZero.length > 0 && (
              <ProductSection
                testId="apps-grid-envicom-4-0"
                label="enviCoM 4.0"
                accentClass="text-envicom-blue"
                dotClass="bg-envicom-blue"
                apps={fourZero}
              />
            )}

            {lite.length > 0 && (
              <ProductSection
                testId="apps-grid-envicom-lite"
                label="enviCoM Lite"
                accentClass="text-envicom-teal"
                dotClass="bg-envicom-teal"
                apps={lite}
              />
            )}

            {apps.length === 0 && (
              <div data-testid="no-apps-msg" className="mt-12 p-6 border border-dashed border-envicom-border rounded text-center text-envicom-muted">
                You don&apos;t have access to any tools yet. Ask the super admin to grant permissions.
              </div>
            )}
          </>
        )}
      </main>
      <footer className="border-t border-envicom-border py-6 text-center text-envicom-muted text-xs font-mono">
        CM Envirosystems · Internal portal · enviCoM 4.0 & Lite
      </footer>
    </div>
  );
}

function ProductSection({ label, accentClass, dotClass, apps, testId }) {
  return (
    <section className="mt-10" data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        <h2 className={`font-mono text-[11px] uppercase tracking-[0.22em] ${accentClass}`}>
          {label}
        </h2>
        <div className="flex-1 h-px bg-envicom-border/60" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {apps.map((app) => (
          <AppCard key={app.id} app={app} locked={app.locked} />
        ))}
      </div>
    </section>
  );
}
